import React, { useCallback, useMemo } from 'react'
import clsx from 'clsx'
import styled, { keyframes } from 'styled-components'
import { rem } from 'polished'

import BodyText from 'LuxKit/Typography/BodyText'
import TextButton from 'LuxKit/Button/TextButton'
import Clickable from 'components/Common/Clickable'
import ResponsiveImage from 'components/Common/ResponsiveImage'
import Image from 'components/Common/Image'

const Sidebar = styled.div`
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-right: 1px solid ${props => props.theme.palette.neutral.default.six};
  background: ${props => props.theme.palette.neutral.default.eight};
`

const SidebarNav = styled.div`
  flex: 1;
  overflow-y: auto;
  min-height: 0;
  padding: ${rem(16)};
  display: flex;
  flex-direction: column;

  > * + * {
    margin-top: ${rem(8)};
  }
`

const SidebarFooter = styled.div`
  flex-shrink: 0;
  padding: ${rem(16)};
  border-top: 1px solid ${props => props.theme.palette.neutral.default.six};
  display: flex;
  flex-direction: column;
  background: ${props => props.theme.palette.neutral.default.eight};

  > * + * {
    margin-top: ${rem(4)};
  }
`

const SidebarItem = styled(Clickable)`
  display: grid;
  grid-template-columns: ${rem(48)} 1fr;
  gap: ${rem(8)};
  align-items: center;
  padding: ${rem(8)};
  border-radius: ${rem(8)};
  background: ${props => props.theme.palette.neutral.default.eight};
  color: ${props => props.theme.palette.neutral.default.two};
  transition: background-color 0.2s, color 0.2s, opacity 0.2s;
  cursor: pointer;

  &.selected {
    background: ${props => props.theme.palette.neutral.default.seven};
    color: ${props => props.theme.palette.neutral.default.one};
  }

  &:hover,
  &:focus-visible {
    background: ${props => props.theme.palette.neutral.default.seven};
    color: ${props => props.theme.palette.neutral.default.one};
  }

  &.muted {
    opacity: 1;
  }
`

const SidebarThumbnail = styled.div`
  width: ${rem(48)};
  height: ${rem(48)};
  border-radius: ${rem(6)};
  overflow: hidden;
  background: ${props => props.theme.palette.neutral.default.six};

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`

const SidebarText = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`

const SidebarTitle = styled(BodyText)`
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`

const SidebarCount = styled(BodyText)`
  color: ${props => props.theme.palette.neutral.default.three};
`

const SkeletonPulse = keyframes`
  0% {
    opacity: 0.5;
  }
  50% {
    opacity: 1;
  }
  100% {
    opacity: 0.5;
  }
`

const SkeletonBlock = styled.div`
  background: ${props => props.theme.palette.neutral.default.six};
  border-radius: ${rem(8)};
  animation: ${SkeletonPulse} 1.4s ease-in-out infinite;
`

const SkeletonLine = styled(SkeletonBlock)`
  height: ${rem(10)};
  width: 100%;
`

const SkeletonThumb = styled(SkeletonBlock)`
  width: ${rem(48)};
  height: ${rem(48)};
  border-radius: ${rem(6)};
`

const SidebarSkeletonItem = styled.div`
  display: grid;
  grid-template-columns: ${rem(48)} 1fr;
  gap: ${rem(8)};
  align-items: center;
  padding: ${rem(8)};
`

const SidebarSkeletonText = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(6)};
`

const SIDEBAR_SKELETON_ITEMS = Array.from({ length: 5 }, (_, index) => index)

interface SidebarSection {
  id: string
  navLabel: string
  countLabel: string
  thumbnail?: App.Image
}

interface Props {
  sections: Array<SidebarSection>
  activeSectionId: string
  onNavClick: (event: React.MouseEvent<HTMLElement>) => void
  isLoading: boolean
  shouldShowRoomSectionSkeletons: boolean
  canShowAvailabilityCta: boolean
  availabilityCtaLabel: string
  onAvailabilityCtaClick: () => void
}

function OfferGallerySectionedSidebar(props: Props) {
  const {
    sections,
    activeSectionId,
    onNavClick,
    isLoading,
    shouldShowRoomSectionSkeletons,
    canShowAvailabilityCta,
    availabilityCtaLabel,
    onAvailabilityCtaClick,
  } = props

  const renderSidebarItem = useCallback((section: SidebarSection) => {
    const isSelected = section.id === activeSectionId
    return (
      <SidebarItem
        key={section.id}
        data-section-id={section.id}
        onClick={onNavClick}
        className={clsx({ selected: isSelected, muted: !isSelected })}
        aria-current={isSelected ? 'true' : undefined}
      >
        <SidebarThumbnail>
          {section.thumbnail?.id && <ResponsiveImage id={section.thumbnail.id} fit="center" />}
          {section.thumbnail?.url && <Image image={section.thumbnail} fit="center" />}
        </SidebarThumbnail>
        <SidebarText>
          <SidebarTitle variant="medium">{section.navLabel}</SidebarTitle>
          <SidebarCount variant="small">{section.countLabel}</SidebarCount>
        </SidebarText>
      </SidebarItem>
    )
  }, [activeSectionId, onNavClick])

  const renderSidebarSkeletonItem = useCallback((index: number) => (
    <SidebarSkeletonItem key={`sidebar-skeleton-${index}`}>
      <SkeletonThumb />
      <SidebarSkeletonText>
        <SkeletonLine />
        <SkeletonLine />
      </SidebarSkeletonText>
    </SidebarSkeletonItem>
  ), [])

  const sidebarItems = useMemo(() => (
    sections.map(renderSidebarItem)
  ), [renderSidebarItem, sections])

  const sidebarSkeletonNodes = useMemo(() => (
    SIDEBAR_SKELETON_ITEMS.map(renderSidebarSkeletonItem)
  ), [renderSidebarSkeletonItem])

  const sidebarContentNodes = useMemo(() => {
    if (isLoading) {
      return sidebarSkeletonNodes
    }
    if (shouldShowRoomSectionSkeletons) {
      return [...sidebarItems, ...sidebarSkeletonNodes]
    }
    return sidebarItems
  }, [isLoading, sidebarItems, sidebarSkeletonNodes, shouldShowRoomSectionSkeletons])

  return (
    <Sidebar>
      <SidebarNav>
        {sidebarContentNodes}
      </SidebarNav>
      <SidebarFooter>
        {canShowAvailabilityCta && (
          <TextButton kind="primary" variant="dark" onClick={onAvailabilityCtaClick}>
            {availabilityCtaLabel}
          </TextButton>
        )}
      </SidebarFooter>
    </Sidebar>
  )
}

export default OfferGallerySectionedSidebar
